import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { AppShellComponent, type ShellHeader } from './app-shell.component';
import type { ShellNavEntry, ShellTab } from './app-shell-nav.type';

/**
 * 인프라 장애/부팅 로딩 오버레이는 있고 없고를 떠나, 이 두 가지가 없는 나머지 값(대부분의
 * 앱은 "network"/"server" 구분 없이 단순 boolean만 필요할 수도 있다)까지 강제하지 않도록
 * `null`을 "장애 아님"으로 둔다 — `SpinoffInfraErrorKind`(`libs/client/spinoff-platform`)와
 * 값 자체는 호환되지만, 이 lib은 auth lib에 의존하지 않으므로 타입을 직접 복제한다.
 */
export type ShellInfraErrorKind = 'network' | 'server' | null;

/**
 * 앱 루트 컴포넌트(`app.ts`/`app.html`)가 반복 hand-roll하던 "인프라 장애 전체화면 →
 * (shellBare 여부에 따라 크롬만 다른) 셸 + `<router-outlet>` + 콘텐츠 영역 부팅 로딩" 조합을
 * 이 컴포넌트 하나로 캡슐화한다. `showBootLoading()`은 셸(헤더·사이드바)을 가리지 않는다 —
 * `<main>` 안쪽 콘텐츠 영역에만 렌더되는 `router-outlet`의 형제 블록이다(2026-08-13, 전체화면
 * 오버레이에서 전환 — refresh API 대기 중에도 헤더·사이드바는 즉시 보여야 한다는 요구).
 * `infraError()`만 여전히 셸 자체를 가리는 전체화면이다 — 재시도가 필요한 terminal 장애라
 * 화면 전체를 막는 게 맞다.
 *
 * 도입 배경(2026-08-11): `sbe-sms/user-web`, `sbe-commerce/user-web`, `schoolbell-e/web`,
 * `schoolbell-e/developer-web` 4개 앱이 각자 이 조합을 손으로 복붙했는데, 그중 3개(sms
 * 제외)가 `shellBare`용 독립 `<router-outlet>`과 셸 안의 `<router-outlet>`을 서로 다른
 * `@if`/`@else` 분기에 두는 실수를 그대로 반복했다 — `shellRouteData()`가 초기값에서
 * 실제 라우트 데이터로 바뀌는 순간 두 outlet 사이를 전환하며 라우트 컴포넌트가 두 번
 * 인스턴스화되는 버그(app-shell-root.component.html 상단 주석 참고)를 안고 있었다.
 * `tools/ci/lint-angular-shell-conventions.mjs`가 앱 루트 템플릿에 `<router-outlet>`이
 * 2개 이상이면 CI에서 바로 잡는다 — 이 컴포넌트를 쓰면 앱 자신의 템플릿엔 outlet이 아예
 * 없으므로(이 컴포넌트 안에 캡슐화) 그 규칙을 구조적으로 통과한다.
 *
 * 이 lib은 auth lib(`spinoff-platform`)에 의존하지 않는다 — `infraError`/`showBootLoading`
 * 둘 다 순수 입력값(어떤 auth 서비스를 쓰든 앱이 계산해서 넘긴다)이라, `spinoff-platform`의
 * `SpinoffAuthService`를 안 쓰는 앱은 그냥 두 입력을 생략하면(기본값 `null`/`false`) 오버레이
 * 없이 바로 셸로 간다 — 예: `schoolbell-e/developer-web`은 자체 `AuthService`
 * (`@sbe-spinoff-schoolbell-e/web-shared`)를 쓰고 로그인 화면 자체가 없어(미인증이면 풀
 * 페이지 리다이렉트로 빠짐) 두 입력을 생략한다.
 *
 * `schoolbell-e/web`은 `SpinoffAuthService`를 쓰므로 sms/commerce와 동일하게
 * `[infraError]="auth.infraError()"`를 바인딩해야 한다 — 생략하면 인터셉터가
 * signal은 세워도 화면엔 아무것도 안 뜨고 콘솔 에러만 남는다(2026-08-21 발견,
 * 로그인 화면에서 실제 재현).
 */
@Component({
  selector: 'sbe-app-shell-root',
  standalone: true,
  imports: [AppShellComponent, MatButtonModule, MatProgressSpinnerModule, RouterOutlet],
  templateUrl: './app-shell-root.component.html',
  styleUrl: './app-shell-root.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppShellRootComponent {
  readonly infraError = input<ShellInfraErrorKind>(null);
  readonly showBootLoading = input(false);
  /** 팝업/리다이렉트 표면(OIDC 콜백, NICE 본인인증 등) — 셸 크롬(toolbar/sidenav/footer)을 건너뛴다. */
  readonly shellBare = input(false);
  readonly header = input<ShellHeader>('toolbar');
  readonly title = input<string | null>(null);
  readonly brandLogo = input<string | null>(null);
  readonly homeRoute = input<string | null>(null);
  readonly navItems = input<readonly ShellNavEntry[]>([]);
  readonly tabs = input<readonly ShellTab[]>([]);
  readonly hasSidenavContent = input(false);
  /** 압축 푸터 판단 기준(로그인 여부) — `AppShellComponent.loggedIn` 그대로 전달. */
  readonly loggedIn = input<boolean | null>(null);
  /** `AppShellComponent.footerVariant` 그대로 전달 — 넘기면 loggedIn 추론을 건너뛴다. */
  readonly footerVariant = input<'full' | 'compact' | null>(null);
  /** `AppShellComponent.containerMaxWidthClass` 그대로 전달 — 기본값도 동일하게 유지. */
  readonly containerMaxWidthClass = input('max-w-7xl');
  /**
   * `<router-outlet>`을 감싸는 래퍼 `<div>`의 클래스 — `[class]` 바인딩이라 완전히 대체된다
   * (누적 아님). 기본값 `'contents'`(`display: contents`)는 이 래퍼가 박스 모델에서
   * 빠져, 라우트 컴포넌트가 (이 래퍼가 없는 것처럼) 셸 안쪽 `<main>`의 직계 flex
   * 아이템으로 남게 한다 — 라우트 컴포넌트가 호스트 엘리먼트 자체에 flex 아이템 클래스를
   * 기대하는 앱(예: `sbe-sms/user-web`의 `HomeComponent`)에 필요하다. 자체 레이아웃
   * 래퍼가 필요한 앱만 이 입력으로 완전히 대체해서 넘긴다.
   */
  readonly contentClass = input('contents');

  /**
   * shellBare일 때만 `false`로 강제하고, 아니면 `null`(오버라이드 안 함)을 넘긴다 —
   * `AppShellComponent`의 `renderFooter`/`renderThemeToggle` 입력은
   * `input() ?? config.renderFooter ?? true`처럼 이 컴포넌트의 입력값을 앱 전역
   * `AppShellConfig`보다 우선한다. 여기서 non-bare 케이스에 `true`를 명시적으로 넘기면
   * (이전 버전의 버그, 코드 리뷰 지적) `provideAppShell({renderFooter: false})`나
   * `{showThemeToggle: false}`로 앱 전역 기본값을 끈 소비 앱이 있어도 이 공용 루트를
   * 거치는 모든 화면에서 그 설정이 조용히 무시된다.
   */
  protected readonly resolvedRenderFooter = computed(() => (this.shellBare() ? false : null));
  protected readonly resolvedRenderThemeToggle = computed(() => (this.shellBare() ? false : null));

  /** infraError 화면의 "다시 시도" 클릭 — 재시도 방법(보통 `window.location.reload()`)은 앱이 결정한다. */
  readonly retry = output<void>();
}
